import {
  addWeeks,
  format,
  getISOWeek,
  getISOWeekYear,
  parseISO,
  startOfISOWeek,
} from 'date-fns'

/** A selectable week in the comparison chart, keyed by its ISO week year + number */
export interface WeekOption {
  /** ISO week number, 1-53 */
  weekNum: number
  /** ISO week year - not always the calendar year of the Monday (week 1 can start in December) */
  year: number
  /** Monday of the week */
  monday: Date
  /** Display label, e.g. "Week 31 (27-July)" */
  label: string
}

export interface WeekOptions {
  /** ISO week years present in the span, most recent first */
  years: number[]
  /** Weeks grouped by ISO week year, most recent first within each year */
  weeksByYear: Map<number, WeekOption[]>
  /** Monday of the earliest selectable week */
  spanStart: Date
  /** Monday of the latest selectable week */
  spanEnd: Date
}

/** Guards the week walk against a pathological date range */
const MAX_WEEKS = 1040

/**
 * Monday of the same ISO week one year earlier, or null when the previous year
 * has no such week (only happens for week 53 following a 52-week year).
 */
export function getSameISOWeekPreviousYear(monday: Date): Date | null {
  const weekNum = getISOWeek(monday)
  const jan4 = new Date(getISOWeekYear(monday) - 1, 0, 4)
  const result = addWeeks(startOfISOWeek(jan4), weekNum - 1)
  return getISOWeek(result) === weekNum ? result : null
}

/** Day of week as Mon=0 ... Sun=6 */
export function dowIndex(date: Date): number {
  const d = date.getDay()
  return d === 0 ? 6 : d - 1
}

/** Percentage change, or null when there is no baseline to compare against */
export function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null
  return Math.round(((current - previous) / previous) * 100)
}

/** e.g. "Week 31 (27-July)" */
export function isoWeekLabel(monday: Date): string {
  return `Week ${getISOWeek(monday)} (${format(monday, 'd-MMMM')})`
}

function toWeekOption(monday: Date): WeekOption {
  return {
    weekNum: getISOWeek(monday),
    year: getISOWeekYear(monday),
    monday,
    label: isoWeekLabel(monday),
  }
}

/**
 * Builds the year/week dropdown options covering every ISO week in the data span.
 *
 * The span runs from the week of the earliest lead to the week of the latest, always
 * extended to include the current week so it stays selectable as the default. Weeks
 * without any leads are kept: they are real weeks that happened to convert nothing,
 * and charting them as flat zero is more honest than hiding them.
 */
export function buildWeekOptions(
  leadsByDate: { date: string }[],
  today: Date = new Date()
): WeekOptions {
  const currentMonday = startOfISOWeek(today)

  let spanStart = currentMonday
  let spanEnd = currentMonday

  if (leadsByDate.length > 0) {
    let earliest = Infinity
    let latest = -Infinity
    for (const { date } of leadsByDate) {
      const t = parseISO(date).getTime()
      if (Number.isNaN(t)) continue
      if (t < earliest) earliest = t
      if (t > latest) latest = t
    }
    if (Number.isFinite(earliest)) {
      const firstMonday = startOfISOWeek(new Date(earliest))
      const lastMonday = startOfISOWeek(new Date(latest))
      if (firstMonday < spanStart) spanStart = firstMonday
      // Future-dated rows (seeded dev data) still get an option rather than vanishing
      if (lastMonday > spanEnd) spanEnd = lastMonday
    }
  }

  const weeksByYear = new Map<number, WeekOption[]>()
  let monday = spanStart
  for (let i = 0; i <= MAX_WEEKS && monday <= spanEnd; i++) {
    const option = toWeekOption(monday)
    const bucket = weeksByYear.get(option.year)
    if (bucket) {
      bucket.push(option)
    } else {
      weeksByYear.set(option.year, [option])
    }
    monday = addWeeks(monday, 1)
  }

  // Most recent first, in both dropdowns
  for (const bucket of weeksByYear.values()) {
    bucket.reverse()
  }
  const years = Array.from(weeksByYear.keys()).sort((a, b) => b - a)

  return { years, weeksByYear, spanStart, spanEnd }
}
